import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  type FC, useCallback, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import {
  itemHeight, itemWidth,
} from '~/_shared/constants/mainMenuUI.constants';
import type { Theme } from '~/_shared/themes/theme.model';
import { useClickOutsideListenerRef } from '~/_shared/utils/hooks/useClickOutsideListenerRef';
import { MainMenuItemInnerMenuComponent } from './mainMenuItemInnerMenu.component';

const MainMenuHeading = styled.div({
  fontSize: '10px',
  textAlign: 'center',
  position: 'absolute',
  padding: '0 4px',
  bottom: -20,
  transition: 'bottom .2s, opacity .2s',
  opacity: 0,
  left: 0,
  width: '100%',
  boxSizing: 'border-box',
});

const IconWrapper = styled.div<{ isHighlighted: boolean }>(({
  theme,
  isHighlighted,
}) => {
  const highlightColor = theme.name === 'light' ? '#B7E1C0' : '#0F3E34';

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    bottom: 0,
    transition: 'bottom .2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&:before': {
      content: '" "',
      position: 'absolute',
      borderRadius: '50%',
      background: isHighlighted ? highlightColor : 'transparent',
      width: 36,
      height: 36,
      left: '50%',
      bottom: '50%',
      transform: 'translate(-50%, 50%)',
    },
  };
});

const itemStyle = ({ isDisabled, isDanger, labelLinesCount, isActive }: {
  isActive: boolean; isDisabled: boolean; isDanger: boolean; labelLinesCount: number;
}) => (theme: Theme) => {
  const enabledColor = isDanger ? theme.textColors.danger : theme.textColors.primary;

  return css({
    position: 'relative',
    flexShrink: 0,
    backgroundColor: isActive ? theme.backgroundColors.highlight : 'inherit',
    borderRadius: 4,
    boxShadow: isActive ? `0 0 0 1px ${theme.borderColors.activeItem} inset` : 'none',
    boxSizing: 'border-box',
    color: isDisabled ? theme.textColors.disabled : enabledColor,
    width: itemWidth,
    overflow: 'hidden',
    height: '100%',
    userSelect: 'none',
    '&:hover': {
      boxShadow: isActive ? `0 0 0 2px ${theme.borderColors.activeItem} inset` : 'none',
      [`${IconWrapper}`]: {
        bottom: labelLinesCount * 12,
      },
      [`${MainMenuHeading}`]: {
        bottom: 7,
        opacity: 1,
      },
    },
  });
};

const wrapperStyle = ({ isDisabled, fontSize }: { isDisabled: boolean; fontSize?: string }) => (theme: Theme) => css({
  backgroundColor: theme.backgroundColors.secondary,
  borderRadius: 4,
  cursor: isDisabled ? 'default' : 'pointer',
  fontSize,
  height: itemHeight,
  display: 'flex',

  '&:hover': {
    backgroundColor: theme.backgroundColors.tertiary,
  },
});

const iconStyle = css({
  position: 'relative',
  fontSize: '20px',
});

export type SingleItemProps = Readonly<{
  icon: IconProp;
  title: string;
  onClick: () => void;
  isDisabled: boolean;
  fontSize?: string;
  testId?: string;
}>;

export type MainMenuItemProps = Readonly<SingleItemProps & {
  innerMenu?: ReadonlyArray<SingleItemProps>;
  isActive?: boolean;
  isHighlighted?: boolean;
  isDisabled: boolean;
  isDanger?: boolean;
  isLoading?: boolean;
  className?: string;
  onMouseEnter?: () => void;
}>;

export const MainMenuItemComponent: FC<MainMenuItemProps> = (props) => {
  const [isInnerItemShown, setIsInnerItemShown] = useState(false);

  const renderMenu = !!(props.innerMenu && isInnerItemShown);

  const clickOutsideRef = useClickOutsideListenerRef(() => {
    setIsInnerItemShown(false);
  }, renderMenu);

  const { onClick } = props;

  const handleClick = useCallback((callback: () => void, isShown: boolean, isDisabled: boolean) => () => {
    if (!isDisabled) {
      setIsInnerItemShown(isShown);
      callback();
    }
  },
  []);

  return (
    <div
      className={props.className}
      css={wrapperStyle({ isDisabled: props.isDisabled, fontSize: props.fontSize })}
    >
      <div
        css={itemStyle({
          isActive: !!props.isActive,
          isDisabled: props.isDisabled,
          isDanger: !!props.isDanger,
          labelLinesCount: Math.ceil(props.title.length / 10),
        })}
        onClick={handleClick(onClick, !isInnerItemShown, props.isDisabled)}
        ref={clickOutsideRef}
      >
        {props.isLoading && <OverlayLoaderComponent loaderSize={30} />}
        <IconWrapper isHighlighted={!!props.isHighlighted}>
          <FontAwesomeIcon
            css={iconStyle}
            icon={props.icon}
          />
        </IconWrapper>

        <MainMenuHeading>
          {props.title}
        </MainMenuHeading>
      </div>

      {renderMenu && (
        <MainMenuItemInnerMenuComponent
          innerMenu={props.innerMenu}
          onItemClick={handleClick}
        />
      )}
    </div>
  );
};
